import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { recognitionAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Loader2, ArrowLeft, Save, Upload, Trash2, Plus, X, Search, UserPlus,
  CheckCircle2, XCircle, RotateCcw, FileText, Image as ImageIcon, Clock, Eye,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: 'coming_soon', label: 'Coming Soon' },
  { value: 'qualification_open', label: 'Qualification Open' },
  { value: 'selection', label: 'Selection in Progress' },
  { value: 'confirmed', label: 'Participants Confirmed' },
  { value: 'completed', label: 'Completed' },
];

const PART_STATUS = {
  invited: { label: 'Invited', cls: 'bg-amber-100 text-amber-800' },
  confirmed: { label: 'Confirmed attendance', cls: 'bg-blue-100 text-blue-800' },
  pending_review: { label: 'Needs review', cls: 'bg-orange-100 text-orange-800' },
  declined: { label: 'Declined', cls: 'bg-slate-100 text-slate-600' },
  approved: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-800' },
  rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-700' },
};

const Txt = ({ label, value, onChange, rows = 3, ...rest }) => (
  <div className="space-y-1.5">
    <Label>{label}</Label>
    <Textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={rows} {...rest} />
  </div>
);

export default function RecognitionProgramEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [program, setProgram] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const coverRef = useRef(null);

  const load = useCallback(() => {
    recognitionAPI.getProgram(id)
      .then((r) => { setProgram(r.data); setForm(r.data); })
      .catch((e) => { toast.error(getErrorMessage(e)); navigate('/recognition/admin'); })
      .finally(() => setLoading(false));
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const payload = {
        name: form.name, edition: form.edition, tagline: form.tagline, description: form.description,
        status: form.status, destination: form.destination, event_dates: form.event_dates,
        qualification_period: form.qualification_period, qualification_details: form.qualification_details,
        rules: form.rules, restrictions: form.restrictions, terms: form.terms, published: form.published,
        prequal_questions: form.prequal_questions || [],
        private_content: form.private_content || {},
        linked_calendar_event_id: form.linked_calendar_event_id || null,
        event_datetime_local: form.event_datetime_local || null,
        event_tz: form.event_tz || 'America/New_York',
        spots: form.spots || null,
        ...extra,
      };
      const r = await recognitionAPI.updateProgram(id, payload);
      setProgram(r.data); setForm(r.data);
      toast.success('Saved');
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setSaving(false); }
  };

  const uploadCover = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    try { const r = await recognitionAPI.uploadCover(id, file); setProgram(r.data); setForm(r.data); toast.success('Cover updated'); }
    catch (err) { toast.error(getErrorMessage(err)); }
  };

  if (loading) return <div className="flex items-center justify-center py-32"><Loader2 className="animate-spin" size={30} /></div>;
  if (!program) return null;

  return (
    <>
      <PageHeader
        title={form.name || 'Program'}
        subtitle="Edit program details, media, participants and the private attendee area"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/recognition/admin')}><ArrowLeft size={16} className="mr-2" /> Back</Button>
            <Button onClick={() => save()} disabled={saving} data-testid="recognition-save-program">
              {saving ? <Loader2 size={15} className="animate-spin mr-2" /> : <Save size={15} className="mr-2" />} Save
            </Button>
          </div>
        }
      />
      <div className="page-content">
        <Tabs defaultValue="details">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
            <TabsTrigger value="media" data-testid="tab-media">Branding & Media</TabsTrigger>
            <TabsTrigger value="prequal" data-testid="tab-prequal">Pre-qualification</TabsTrigger>
            <TabsTrigger value="private" data-testid="tab-private">Private Area</TabsTrigger>
            <TabsTrigger value="participants" data-testid="tab-participants">Participants</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
          </TabsList>

          {/* DETAILS */}
          <TabsContent value="details" className="mt-5 space-y-4 max-w-3xl">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Name *</Label><Input value={form.name || ''} onChange={(e) => upd('name', e.target.value)} data-testid="prog-name" /></div>
              <div className="space-y-1.5"><Label>Edition</Label><Input value={form.edition || ''} onChange={(e) => upd('edition', e.target.value)} /></div>
            </div>
            <div className="space-y-1.5"><Label>Tagline</Label><Input value={form.tagline || ''} onChange={(e) => upd('tagline', e.target.value)} placeholder="An Exclusive Annual Experience" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => upd('status', v)}>
                  <SelectTrigger data-testid="prog-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-end"><div className="flex items-center justify-between w-full rounded-lg border p-2.5 h-10">
                <Label className="text-sm">Published (visible to agents)</Label>
                <Switch checked={!!form.published} onCheckedChange={(v) => upd('published', v)} data-testid="prog-published" /></div></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5"><Label>Destination</Label><Input value={form.destination || ''} onChange={(e) => upd('destination', e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Event Dates</Label><Input value={form.event_dates || ''} onChange={(e) => upd('event_dates', e.target.value)} placeholder="May 2027" /></div>
              <div className="space-y-1.5"><Label>Qualification Period</Label><Input value={form.qualification_period || ''} onChange={(e) => upd('qualification_period', e.target.value)} placeholder="To Be Announced" /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5"><Label>Available Spots</Label><Input value={form.spots || ''} onChange={(e) => upd('spots', e.target.value)} placeholder="5 Agents (each with one guest)" data-testid="prog-spots" /></div>
              <div className="space-y-1.5"><Label>Exact event date (enables countdown)</Label><Input type="datetime-local" value={form.event_datetime_local || ''} onChange={(e) => upd('event_datetime_local', e.target.value)} data-testid="prog-event-datetime" /></div>
              <div className="space-y-1.5"><Label>Event timezone</Label>
                <Select value={form.event_tz || 'America/New_York'} onValueChange={(v) => upd('event_tz', v)}>
                  <SelectTrigger data-testid="prog-event-tz"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="America/New_York">Eastern (FL / New York)</SelectItem>
                    <SelectItem value="America/Chicago">Central (Chicago)</SelectItem>
                    <SelectItem value="America/Denver">Mountain (Denver)</SelectItem>
                    <SelectItem value="America/Los_Angeles">Pacific (Los Angeles)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-[11px] text-slate-400 -mt-2">Leave the exact date empty to show “THE EXPERIENCE IS COMING” instead of a live countdown.</p>
            <Txt label="Description" value={form.description} onChange={(v) => upd('description', v)} rows={4} placeholder="Describe the experience. Keep unconfirmed details clearly preliminary." />
            <Txt label="How to Qualify" value={form.qualification_details} onChange={(v) => upd('qualification_details', v)} rows={4} />
            <Txt label="Official Rules" value={form.rules} onChange={(v) => upd('rules', v)} rows={4} />
            <Txt label="Restrictions" value={form.restrictions} onChange={(v) => upd('restrictions', v)} rows={3} />
            <Txt label="Terms & Conditions" value={form.terms} onChange={(v) => upd('terms', v)} rows={3} />
            <div className="space-y-1.5"><Label>Linked calendar event ID (optional)</Label>
              <Input value={form.linked_calendar_event_id || ''} onChange={(e) => upd('linked_calendar_event_id', e.target.value)} placeholder="Paste an event ID to link this program" data-testid="prog-linked-event" />
              <p className="text-[11px] text-slate-400">Optional. Link this program to a calendar event. You can also point a dashboard banner here: <code>/recognition/{id}</code></p>
            </div>
          </TabsContent>

          {/* MEDIA */}
          <TabsContent value="media" className="mt-5 space-y-6 max-w-3xl">
            <div>
              <Label className="mb-2 block">Cover image (editable)</Label>
              <div className="rounded-xl border overflow-hidden bg-slate-50 max-w-xl">
                {form.cover_image_url ? <img src={`${BACKEND}${form.cover_image_url}`} alt="cover" className="w-full h-auto" />
                  : <div className="h-40 flex items-center justify-center text-slate-400 text-sm">No cover yet</div>}
              </div>
              <input ref={coverRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={uploadCover} data-testid="cover-input" />
              <Button variant="outline" size="sm" className="mt-2" onClick={() => coverRef.current?.click()} data-testid="cover-upload-btn"><Upload size={14} className="mr-2" /> Upload cover</Button>
            </div>
            <MediaBucket programId={id} program={program} bucket="public_doc" title="Public program materials (PDF/images, visible to all)" onChange={load} accept="image/*,application/pdf" />
          </TabsContent>

          {/* PRE-QUALIFICATION */}
          <TabsContent value="prequal" className="mt-5 max-w-2xl">
            <PrequalEditor form={form} setForm={setForm} onSave={() => save()} saving={saving} />
          </TabsContent>

          {/* PRIVATE AREA */}
          <TabsContent value="private" className="mt-5 space-y-4 max-w-3xl">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 flex items-center justify-between gap-3">
              <span>This content unlocks only for participants you have <b>approved</b>, via the “Enter” button.</span>
              <Button variant="outline" size="sm" className="shrink-0" onClick={() => window.open(`/recognition/${id}?preview=private`, '_blank', 'noopener')} data-testid="preview-private-btn">
                <Eye size={14} className="mr-1" /> Preview attendee view
              </Button>
            </div>
            <Txt label="Trip Details" value={form.private_content?.trip_details} onChange={(v) => upd('private_content', { ...(form.private_content || {}), trip_details: v })} rows={4} />
            <Txt label="Itinerary" value={form.private_content?.itinerary} onChange={(v) => upd('private_content', { ...(form.private_content || {}), itinerary: v })} rows={4} />
            <Txt label="Schedule" value={form.private_content?.schedule} onChange={(v) => upd('private_content', { ...(form.private_content || {}), schedule: v })} rows={4} />
            <Txt label="Requirements" value={form.private_content?.requirements} onChange={(v) => upd('private_content', { ...(form.private_content || {}), requirements: v })} rows={3} />
            <Button onClick={() => save()} disabled={saving} data-testid="save-private"><Save size={15} className="mr-2" /> Save private content</Button>
            <MediaBucket programId={id} program={program} bucket="private_photo" title="Destination photos (private)" onChange={load} accept="image/*" />
            <MediaBucket programId={id} program={program} bucket="private_doc" title="Private documents" onChange={load} accept="image/*,application/pdf" />
          </TabsContent>

          {/* PARTICIPANTS */}
          <TabsContent value="participants" className="mt-5">
            <ParticipantsManager programId={id} />
          </TabsContent>

          {/* HISTORY */}
          <TabsContent value="history" className="mt-5 max-w-3xl">
            <AuditLog programId={id} />
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}

function MediaBucket({ programId, program, bucket, title, onChange, accept }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const items = (program.media || []).filter((m) => m.bucket === bucket);
  const upload = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    setBusy(true);
    try { await recognitionAPI.uploadMedia(programId, bucket, file); toast.success('Uploaded'); onChange(); }
    catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(false); }
  };
  const del = async (mid) => {
    try { await recognitionAPI.deleteMedia(programId, mid); toast.success('Removed'); onChange(); }
    catch (err) { toast.error(getErrorMessage(err)); }
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label>{title}</Label>
        <input ref={ref} type="file" accept={accept} className="hidden" onChange={upload} data-testid={`media-input-${bucket}`} />
        <Button variant="outline" size="sm" onClick={() => ref.current?.click()} disabled={busy} data-testid={`media-upload-${bucket}`}>
          {busy ? <Loader2 size={13} className="animate-spin mr-1" /> : <Upload size={13} className="mr-1" />} Add
        </Button>
      </div>
      {items.length === 0 ? <p className="text-xs text-slate-400">Nothing uploaded yet.</p> : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {items.map((m) => (
            <div key={m.media_id} className="relative rounded-lg border overflow-hidden bg-slate-50 group">
              {m.mime?.startsWith('image/') ? <img src={`${BACKEND}${m.url}`} alt={m.name} className="w-full h-24 object-cover" />
                : <div className="h-24 flex flex-col items-center justify-center text-slate-500 text-[10px] p-2"><FileText size={20} /><span className="truncate w-full text-center mt-1">{m.name}</span></div>}
              <button onClick={() => del(m.media_id)} className="absolute top-1 right-1 bg-white/90 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`media-del-${m.media_id}`}><Trash2 size={12} className="text-red-500" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PrequalEditor({ form, setForm, onSave, saving }) {
  const questions = form.prequal_questions || [];
  const addQ = () => setForm((p) => ({ ...p, prequal_questions: [...(p.prequal_questions || []), { id: Math.random().toString(36).slice(2, 10), text: '' }] }));
  const updQ = (i, text) => setForm((p) => { const q = [...(p.prequal_questions || [])]; q[i] = { ...q[i], text }; return { ...p, prequal_questions: q }; });
  const delQ = (i) => setForm((p) => ({ ...p, prequal_questions: (p.prequal_questions || []).filter((_, idx) => idx !== i) }));
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-500">Yes/No questions a selected agent must answer. Any “No” sends them to <b>Needs review</b> instead of auto-confirming.</p>
      {questions.map((q, i) => (
        <div key={q.id} className="flex items-center gap-2" data-testid={`prequal-row-${i}`}>
          <Input value={q.text} onChange={(e) => updQ(i, e.target.value)} placeholder="e.g. Do you have a valid passport?" data-testid={`prequal-input-${i}`} />
          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => delQ(i)}><X size={15} /></Button>
        </div>
      ))}
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={addQ} data-testid="prequal-add"><Plus size={14} className="mr-1" /> Add question</Button>
        <Button size="sm" onClick={onSave} disabled={saving} data-testid="prequal-save">{saving ? <Loader2 size={13} className="animate-spin mr-1" /> : <Save size={13} className="mr-1" />} Save</Button>
      </div>
    </div>
  );
}

function ParticipantsManager({ programId }) {
  const [loading, setLoading] = useState(true);
  const [participants, setParticipants] = useState([]);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);

  const load = useCallback(() => {
    recognitionAPI.listParticipants(programId)
      .then((r) => setParticipants(Array.isArray(r.data) ? r.data : []))
      .catch((e) => toast.error(getErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [programId]);
  useEffect(() => { load(); }, [load]);

  const search = async (val) => {
    setQ(val);
    if (val.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    try { const r = await recognitionAPI.agentSearch(val); setResults(r.data || []); }
    catch { setResults([]); } finally { setSearching(false); }
  };

  const add = async (agent) => {
    try { await recognitionAPI.addParticipant(programId, { agent_npn: agent.npn, agent_name: agent.name }); toast.success(`${agent.name} invited`); setQ(''); setResults([]); load(); }
    catch (e) { toast.error(getErrorMessage(e)); }
  };

  const decide = async (pid, decision) => {
    try { await recognitionAPI.decideParticipant(pid, { decision }); toast.success('Updated'); load(); }
    catch (e) { toast.error(getErrorMessage(e)); }
  };

  const remove = async () => {
    try { await recognitionAPI.removeParticipant(removeTarget.participant_id); toast.success('Removed'); setRemoveTarget(null); load(); }
    catch (e) { toast.error(getErrorMessage(e)); }
  };

  return (
    <div className="max-w-4xl">
      <Card className="mb-5">
        <CardContent className="py-4">
          <Label className="mb-2 block">Select participants (search by name or NPN)</Label>
          <div className="relative">
            <Search size={15} className="absolute left-3 top-2.5 text-slate-400" />
            <Input value={q} onChange={(e) => search(e.target.value)} placeholder="Type at least 2 characters…" className="pl-9" data-testid="participants-search" />
          </div>
          {searching && <p className="text-xs text-slate-400 mt-1">Searching…</p>}
          {results.length > 0 && (
            <div className="mt-2 border rounded-lg divide-y max-h-56 overflow-y-auto">
              {results.map((a) => (
                <button key={a.npn} onClick={() => add(a)} className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-50 text-left" data-testid={`agent-result-${a.npn}`}>
                  <span className="text-sm text-slate-700">{a.name} <span className="text-xs text-slate-400">NPN {a.npn}</span></span>
                  <UserPlus size={15} className="text-[#1a4e88]" />
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {loading ? <div className="flex justify-center py-10"><Loader2 className="animate-spin" size={24} /></div>
        : participants.length === 0 ? <p className="text-sm text-slate-400 text-center py-8">No participants selected yet.</p>
        : (
          <div className="space-y-2">
            {participants.map((p) => {
              const st = PART_STATUS[p.status] || PART_STATUS.invited;
              return (
                <Card key={p.participant_id} data-testid={`participant-${p.participant_id}`}>
                  <CardContent className="py-3">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900 text-sm">{p.agent_name}</span>
                          <span className="text-xs text-slate-400">NPN {p.agent_npn}</span>
                          <Badge className={`text-[10px] ${st.cls}`}>{st.label}</Badge>
                          {p.attend === true && <Badge variant="outline" className="text-[10px] text-emerald-700">Can attend</Badge>}
                          {p.attend === false && <Badge variant="outline" className="text-[10px] text-red-600">Can't attend</Badge>}
                        </div>
                        {p.prequal_answers?.length > 0 && (
                          <p className="text-[11px] text-slate-500 mt-1">
                            Pre-qual: {p.prequal_answers.filter((a) => a.answer).length}/{p.prequal_answers.length} Yes
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {p.status !== 'approved' && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8" onClick={() => decide(p.participant_id, 'approve')} data-testid={`approve-${p.participant_id}`}><CheckCircle2 size={14} className="mr-1" /> Approve</Button>}
                        {p.status !== 'rejected' && <Button size="sm" variant="outline" className="h-8 text-red-600" onClick={() => decide(p.participant_id, 'reject')} data-testid={`reject-${p.participant_id}`}><XCircle size={14} className="mr-1" /> Reject</Button>}
                        <Button size="sm" variant="ghost" className="h-8" onClick={() => decide(p.participant_id, 'reset')} title="Reset to invited"><RotateCcw size={14} /></Button>
                        <Button size="sm" variant="ghost" className="h-8 text-slate-400" onClick={() => setRemoveTarget(p)}><Trash2 size={14} /></Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

      <AlertDialog open={!!removeTarget} onOpenChange={() => setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Remove participant</AlertDialogTitle>
            <AlertDialogDescription>Remove {removeTarget?.agent_name} from this program?</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-500 hover:bg-red-600">Remove</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AuditLog({ programId }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  useEffect(() => {
    recognitionAPI.getAudit(programId).then((r) => setRows(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [programId]);
  if (loading) return <div className="flex justify-center py-10"><Loader2 className="animate-spin" size={22} /></div>;
  if (rows.length === 0) return <p className="text-sm text-slate-400 text-center py-8">No history yet.</p>;
  return (
    <div className="space-y-2">
      {rows.map((r) => (
        <div key={r.audit_id} className="flex items-start gap-3 text-sm border-b pb-2" data-testid="audit-row">
          <Clock size={14} className="text-slate-400 mt-0.5 shrink-0" />
          <div>
            <span className="font-medium text-slate-700">{r.action.replace(/_/g, ' ')}</span>
            {r.detail && <span className="text-slate-500"> — {r.detail}</span>}
            <div className="text-[11px] text-slate-400">{r.by_name} · {new Date(r.at).toLocaleString()}</div>
          </div>
        </div>
      ))}
    </div>
  );
}
